import type { CitationTarget, FileRecord } from '@reados/contracts';

export interface TimeLocation { start: number; end?: number }
export interface ResolvedReference {
  target: CitationTarget; kind: FileRecord['kind']; resourceUrl: string; label: string;
  /** Accepted image bytes and their stable identity survive ticket renewal. */
  contentRevision?: string; sourceIdentity?: string;
}
const CONTROL = /[\u0000-\u001f\u007f]/;

export function canonicalWorkspacePath(raw: string): string | null {
  let value: string;
  try { value = decodeURIComponent(raw); } catch { return null; }
  if (CONTROL.test(value) || value.includes('\\') || !value.startsWith('/workspace/')) return null;
  if (value.slice(1).split('/').some(part => part === '..' || part === '.' || part === '')) return null;
  return value;
}

export function timestampSeconds(raw: string): number | null {
  const value = raw.trim();
  if (!value || !/^[\d.:]+$/.test(value)) return null;
  const parts = value.split(':').map(Number);
  if (parts.some(n => !Number.isFinite(n) || n < 0) || parts.length > 3) return null;
  if (parts.length > 1 && parts.at(-1)! >= 60) return null;
  if (parts.length === 3 && parts[1] >= 60) return null;
  return parts.reduce((total, n) => total * 60 + n, 0);
}

export function parseTimeLocations(raw: string): TimeLocation[] {
  const seen = new Set<string>();
  return raw.split(/[,，、;；]/).flatMap(value => {
    const pair = value.trim().split(/\s*[-–—~～至到]\s*/);
    if (pair.length > 2) return [];
    const start = timestampSeconds(pair[0]);
    const end = pair.length === 2 ? timestampSeconds(pair[1]) : undefined;
    if (start === null || end === null || end !== undefined && end < start) return [];
    const key = `${start}:${end ?? ''}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ start, ...(end === undefined ? {} : { end }) }];
  });
}

function boundedPageLocations(raw: string): string[] {
  return raw.split(/[,，、;；]/).map(s => s.trim()).filter(s => s.length > 0 && s.length <= 128).slice(0, 200);
}

function fileAtExactPath(path: string, files: FileRecord[]): FileRecord | null | undefined {
  const current = files.find(file => file.sandboxPath === path);
  if (current) return current;
  // Prior paths are server-projected, revision-bound migration receipts.
  const matches = files.filter(file => Array.isArray(file.referencePaths) && file.referencePaths.includes(path));
  return matches.length > 1 ? null : matches[0];
}

function pathKind(path: string): FileRecord['kind'] {
  const extension = path.split('.').at(-1)?.toLowerCase();
  return /^(mp4|m4v|webm|mov|ogv)$/.test(extension || '') ? 'video'
    : /^(png|jpg|jpeg|webp|gif|avif)$/.test(extension || '') ? 'image'
    : extension === 'pdf' ? 'pdf' : 'other';
}

interface PathLines { path: string; start: string; end?: string }
function pathLineCandidates(path: string): { candidates: PathLines[]; fallback?: PathLines } {
  // A colon suffix is location syntax only after the complete filename has
  // failed exact identity resolution. line:column retains its line because
  // CitationTarget currently has no column navigation field.
  const structured = /^(.+):(\d+)(?:-(\d+)|:(\d+))$/.exec(path);
  const single = /^(.+):(\d+)$/.exec(path);
  const parse = (match: RegExpExecArray | null): PathLines | undefined => {
    if (!match || match[1].endsWith('/')) return;
    const [start, end, column] = match.slice(2).map(value => value === undefined ? undefined : Number(value));
    if (!/^[1-9]\d*$/.test(match[2]) || !Number.isSafeInteger(start)
      || end !== undefined && (!/^[1-9]\d*$/.test(match[3]) || !Number.isSafeInteger(end) || end < start!)
      || column !== undefined && (!/^[1-9]\d*$/.test(match[4]) || !Number.isSafeInteger(column))) return;
    return { path: match[1], start: match[2], ...(match[3] ? { end: match[3] } : {}) };
  };
  const fallback = parse(structured ?? single);
  // For `real:name:5:3`, prefer a verified `real:name:5` filename to
  // interpreting its last two components as line and column.
  const candidates = [parse(single), parse(structured)].filter((value): value is PathLines => !!value);
  return { candidates, fallback };
}

/** Resolves exact upload identities. Generated artifacts keep their canonical path;
 * the authenticated API remains the authority for containment/existence. */
export function resolveReference(raw: string, files: FileRecord[], origin: string, documentPath?: string): ResolvedReference | null {
  if (typeof raw !== 'string' || raw.length > 8192 || CONTROL.test(raw)) return null;
  let url: URL;
  try {
    // Resolve document-relative destinations at the host boundary. The source
    // program stays untouched, including reference definitions and DIL strings.
    // A canonical workspace base is mandatory; URL normalization can consume
    // ../, but the resulting destination must still pass workspace containment.
    const encodedBase = documentPath?.split('/').map(encodeURIComponent).join('/');
    const base = encodedBase && canonicalWorkspacePath(encodedBase);
    if (documentPath && !base || raw.includes('\\')) return null;
    url = new URL(raw, base ? new URL(encodedBase!, origin) : origin);
  } catch { return null; }
  if (!['https:', 'http:', 'readex:'].includes(url.protocol)) return null;
  const internal = url.protocol === 'readex:' || url.pathname === '/.readex/content-reference';
  let path = internal ? url.searchParams.get('path') || '' : url.pathname;
  const byId = internal ? files.find(f => f.id === (url.searchParams.get('content_id') || url.searchParams.get('document_id'))) : undefined;
  let contentRevision: string | undefined;
  if (url.protocol !== 'readex:' && url.origin !== origin) return null;
  if (byId) path = byId.sandboxPath;
  if (url.pathname.startsWith('/api/files/')) {
    const match = /^\/api\/files\/([^/]+)\/content$/.exec(url.pathname);
    let identity = '';
    try { identity = match ? decodeURIComponent(match[1]) : ''; } catch { return null; }
    const file = files.find(f => f.id === identity);
    if (!file) return null;
    if (url.searchParams.has('contentRevision')) {
      const revision = url.searchParams.get('contentRevision')!;
      if (!/^[a-f0-9]{64}$/.test(revision) || file.contentRevision !== revision) return null;
      contentRevision = revision;
    }
    path = file.sandboxPath;
  } else if (url.pathname === '/api/artifacts/content') {
    path = url.searchParams.get('path') || '';
  }
  let canonical = canonicalWorkspacePath(path);
  if (!canonical) return null;
  let file = byId ?? fileAtExactPath(canonical, files);
  if (file === null) return null;
  let pathLines: PathLines | undefined;
  if (!file) {
    const { candidates, fallback } = pathLineCandidates(canonical);
    for (const candidate of candidates) {
      const match = fileAtExactPath(candidate.path, files);
      if (match === null) return null;
      if (match && (match.kind === 'text' || match.kind === 'other')) {
        file = match;
        pathLines = candidate;
        break;
      }
    }
    // Generated text/code artifacts have no catalog entry. Keep the complete
    // canonical path apart from its explicit line suffix; existence and
    // containment remain the authenticated server's responsibility.
    if (!pathLines && fallback && pathKind(fallback.path) === 'other') pathLines = fallback;
    if (pathLines) canonical = pathLines.path;
  }
  const kind = file?.kind ?? pathKind(canonical);
  if (kind === 'video' && /^[a-f0-9]{64}$/.test(file?.contentRevision || '')) contentRevision = file!.contentRevision;
  const fragment = new URLSearchParams(url.hash.slice(1));
  const get = (...keys: string[]) => keys.map(key => fragment.get(key) ?? url.searchParams.get(key)).find(Boolean) || '';
  const target: CitationTarget = { ...(file ? { fileId: file.id } : {}), path: file?.sandboxPath ?? canonical };
  if (kind === 'pdf') {
    const locations = boundedPageLocations(get('p', 'page_locations', 'page_label', 'pageLabel'));
    if (locations.length) {
      const range = /^(\d+|[ivxlcdm]+)-(\d+|[ivxlcdm]+)$/i.exec(locations[0]);
      target.pageLabel = range?.[1] ?? locations[0]; target.pageLocations = locations;
    }
    const physical = Number(get('page_number', 'pageNumber', 'page'));
    if (Number.isSafeInteger(physical) && physical > 0) target.pageNumber = physical;
  }
  if (kind === 'video') {
    const rawTimes = get('t', 'time_locations', 'timeLocations', 'time', 'timestamp', 'range');
    const start = get('start', 'start_seconds', 'startSeconds');
    const end = get('end', 'end_seconds', 'endSeconds');
    const locations = parseTimeLocations(rawTimes || (start ? start + (end ? '-' + end : '') : ''));
    if (locations.length) { target.start = locations[0].start; target.end = locations[0].end; target.timeLocations = locations; }
  }
  if (kind === 'text' || kind === 'other') {
    const lines = /^#L([1-9]\d*)(?:-L?([1-9]\d*))?$/.exec(url.hash);
    const explicitStart = get('line'), explicitEnd = get('endLine');
    const explicit = !!(explicitStart || explicitEnd);
    const startValue = lines?.[1] ?? (explicit ? explicitStart : pathLines?.start ?? '');
    const endValue = lines ? lines[2] || '' : explicit ? explicitEnd : pathLines?.end ?? '';
    if (/^[1-9]\d*$/.test(startValue) && (!endValue || /^[1-9]\d*$/.test(endValue))) {
      const start = Number(startValue), end = endValue ? Number(endValue) : undefined;
      if (Number.isSafeInteger(start) && (end === undefined || Number.isSafeInteger(end) && end >= start)) {
        target.lineStart = start;
        if (end !== undefined) target.lineEnd = end;
      }
    }
  }
  const resource = new URL(file ? `/api/files/${encodeURIComponent(file.id)}/content`
    : `/api/artifacts/content?path=${encodeURIComponent(canonical)}`, origin).href;
  const resourceUrl = contentRevision ? `${resource}?contentRevision=${contentRevision}` : resource;
  return { target, kind, resourceUrl, label: file?.name ?? canonical.split('/').at(-1) ?? '文件',
    ...(contentRevision ? { contentRevision, sourceIdentity: resourceUrl } : {}) };
}

/** Build only the whitelisted navigation shape, never forward model objects. */
export function targetFromPayload(value: unknown, files: FileRecord[], origin: string, documentPath?: string): ResolvedReference | null {
  if (!value || typeof value !== 'object') return null;
  const object = value as Record<string, unknown>;
  const path = typeof object.path === 'string' ? object.path : '';
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  for (const [input, output] of [['contentID', 'content_id'], ['documentID', 'document_id'], ['pageLabel', 'page_label'], ['pageNumber', 'page_number'], ['startSeconds', 'start'], ['endSeconds', 'end'], ['lineNumber', 'line'], ['endLineNumber', 'endLine']] as const) {
    const v = object[input];
    if (typeof v === 'string' || typeof v === 'number' && Number.isFinite(v)) params.set(output, String(v));
  }
  const videoLocations = Array.isArray(object.videoTimeLocations) ? object.videoTimeLocations : object.timeLocations;
  if (Array.isArray(videoLocations)) {
    const entries = videoLocations.slice(0, 200).flatMap(value => {
      if (!value || typeof value !== 'object') return [];
      const item = value as Record<string, unknown>, start = item.startSeconds ?? item.start, end = item.endSeconds ?? item.end;
      return typeof start === 'number' && Number.isFinite(start) && start >= 0
        && (end === undefined || typeof end === 'number' && Number.isFinite(end) && end >= start)
        ? [String(start) + (end === undefined ? '' : '-' + end)] : [];
    });
    if (entries.length) params.set('time_locations', entries.join(','));
  }
  if (Array.isArray(object.pageLocations)) params.set('page_locations', object.pageLocations.filter(v => typeof v === 'string').slice(0, 200).join(','));
  const base = params.size ? resolveReference(`/.readex/content-reference?${params}`, files, origin) : null;
  return base ?? (typeof object.url === 'string' ? resolveReference(object.url, files, origin, documentPath) : null);
}
